import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';

export interface FilterDropdownRef {
  toggle: () => void;
  close: () => void;
  open: () => void;
}

interface FilterDropdownProps {
  label: string;
  hasActiveFilter?: boolean;
  onClearFilter?: () => void;
  onDropdownToggle?: (isOpen: boolean) => void;
  children: React.ReactNode;
}

const FilterDropdown = forwardRef<FilterDropdownRef, FilterDropdownProps>(({
  label,
  hasActiveFilter = false,
  onClearFilter,
  onDropdownToggle,
  children
}, ref) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    toggle: () => {
      updateDropdownState(!isDropdownOpen);
    },
    close: () => {
      updateDropdownState(false);
    },
    open: () => {
      updateDropdownState(true);
    }
  }), [isDropdownOpen]);

  const updateDropdownState = (newIsOpen: boolean) => {
    setIsDropdownOpen(newIsOpen);
    if (onDropdownToggle) {
      onDropdownToggle(newIsOpen);
    }
  };

  // Handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        updateDropdownState(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleDropdown = () => {
    updateDropdownState(!isDropdownOpen);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        className="flex items-center cursor-pointer hover:text-gray-600"
        onClick={handleToggleDropdown}
      >
        {hasActiveFilter && (
          <i className="mr-1 text-[0.5rem] fa fa-circle text-gray-600" title="Filter active"/>
        )}
        <span>{label}</span>
        <i className={`ml-1 text-xs fa fa-chevron-${isDropdownOpen ? 'up' : 'down'}`}/>
      </div>

      {isDropdownOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 py-4 min-w-64">
          {children}
        </div>
      )}
    </div>
  );
});

FilterDropdown.displayName = 'FilterDropdown';
export default FilterDropdown;
